import React from "react";
import { useTranslation } from "react-i18next";
import { AppSettings, ExtensionsStatusData, ExtensionType, QuotaModel, StorageQuotaMode } from "../../types";

interface QuotaModelsSectionProps {
  settings: AppSettings | null;
  extensionsStatus: ExtensionsStatusData | null;
  quotaModels: QuotaModel[];
  loadingQuotaModels: boolean;
  saving: boolean;
  verifyingExtension: ExtensionType | null;
  onActivateExtension: (extensionKey: ExtensionType) => void;
  onVerifyExtension: (extensionKey: ExtensionType) => void;
  onChangeQuotaMode: (mode: StorageQuotaMode) => void;
  onOpenCreateModal: () => void;
  onOpenEditModal: (model: QuotaModel) => void;
}

export const QuotaModelsSection: React.FC<QuotaModelsSectionProps> = ({
  settings,
  extensionsStatus,
  quotaModels,
  loadingQuotaModels,
  saving,
  verifyingExtension,
  onActivateExtension,
  onVerifyExtension,
  onChangeQuotaMode,
  onOpenCreateModal,
  onOpenEditModal,
}) => {
  const { t } = useTranslation('adminPage');

  const isPremium = extensionsStatus?.featureDetails?.find(
    (f) => f.key === 'quota'
  )?.isPremium ?? false;

  const hasPaidAccess = !isPremium || Boolean(extensionsStatus?.isPaidTier);

  return (
    <div className="setup-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <h2>{t('sections.quotaModels.title')}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {isPremium && (
            <span className="admin-premium-badge">
              {t('sections.quotaExtension.premiumBadge')}
            </span>
          )}
          {hasPaidAccess && (
            extensionsStatus?.extensions?.sharepoint?.consented ? (
              <span className="admin-extension-badge">
                {t('sections.quotaExtension.extensionActive')}
              </span>
            ) : (
              <span className="admin-premium-badge">
                ⚠️ {t('sections.quotaExtension.warningTitle')}
              </span>
            )
          )}
        </div>
      </div>
      <p className="help-text">{t('sections.quotaModels.description')}</p>

      {/* Bannière d'avertissement Free Plan ou Extension SharePoint manquante */}
      {!hasPaidAccess ? (
        <div className="admin-extension-banner premium admin-mb-16">
          <div className="admin-extension-banner-header">
            <strong>{t('sections.quotaExtension.premiumBadge')}</strong>
          </div>
          <p className="admin-extension-banner-desc">
            {t('sections.quotaExtension.premiumDescription')}
          </p>
          <div className="admin-extension-actions">
            <button
              className="btn btn-primary btn-small"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {t('sections.securityGroup.upgradeButton')}
            </button>
          </div>
        </div>
      ) : !extensionsStatus?.extensions?.sharepoint?.consented ? (
        <div className="admin-extension-banner warning admin-mb-16">
          <div className="admin-extension-banner-header">
            <strong>⚠️ {t('sections.quotaExtension.warningTitle')}</strong>
          </div>
          <p className="admin-extension-banner-desc">
            {t('sections.quotaExtension.warningDescription')}
          </p>
          <div className="admin-extension-actions">
            <button
              className="btn btn-primary btn-small"
              onClick={() => onActivateExtension('sharepoint')}
            >
              {t('sections.quotaExtension.activateExtension')}
            </button>
            <button
              className="btn btn-secondary btn-small"
              onClick={() => onVerifyExtension('sharepoint')}
              disabled={verifyingExtension === 'sharepoint'}
            >
              {verifyingExtension === 'sharepoint' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span className="admin-spinner-inline" />
                  {t('sections.quotaExtension.verifying')}
                </span>
              ) : (
                t('sections.quotaExtension.verifyExtension')
              )}
            </button>
          </div>
        </div>
      ) : null}

      {/* Choix du mode de quota */}
      <div className="status-card admin-mb-16">
        <div className="status-content">
          <label className="admin-approval-mode-label">
            {t('sections.quotaModels.modeLabel')}
          </label>
          <div className={`admin-approval-options-container${saving ? ' saving' : ''}`}>
            {(['NoQuota', 'QuotaOptional', 'QuotaMandatory'] as StorageQuotaMode[]).map((mode) => {
              const sharePointExtensionActive = hasPaidAccess && extensionsStatus?.extensions?.sharepoint?.consented;
              const effectiveMode = sharePointExtensionActive ? (settings?.storageQuotaMode ?? 'NoQuota') : 'NoQuota';
              const isOptionDisabled = mode === 'NoQuota' ? saving : (saving || !sharePointExtensionActive);
              return (
                <label
                  key={mode}
                  className={`admin-radio-option${effectiveMode === mode ? ' selected' : ''}${isOptionDisabled ? ' disabled' : ''}`}
                >
                  <input
                    type="radio"
                    name="storageQuotaMode"
                    value={mode}
                    checked={effectiveMode === mode}
                    onChange={() => onChangeQuotaMode(mode)}
                    disabled={isOptionDisabled}
                    className="admin-radio-input"
                  />
                  <div className="admin-radio-content">
                    <div className="admin-radio-title">{t(`sections.quotaModels.modes.${mode}.title`)}</div>
                    <div className="admin-radio-desc">{t(`sections.quotaModels.modes.${mode}.description`)}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Liste des modèles (uniquement si mode != NoQuota) */}
      {hasPaidAccess &&
        extensionsStatus?.extensions?.sharepoint?.consented &&
        (settings?.storageQuotaMode === 'QuotaOptional' || settings?.storageQuotaMode === 'QuotaMandatory') && (
          <div className="status-card">
            <div className="status-content">
              <div className="admin-status-header">
                <h3 className="admin-section-title">
                  {t('sections.quotaModels.count', { count: quotaModels.length })}
                </h3>
                <button
                  onClick={onOpenCreateModal}
                  disabled={loadingQuotaModels || !(hasPaidAccess && extensionsStatus?.extensions?.sharepoint?.consented)}
                  className="admin-add-btn"
                >
                  {t('sections.quotaModels.add')}
                </button>
              </div>

              {quotaModels.length > 0 ? (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('sections.quotaModels.tableHeaders.name')}</th>
                        <th>{t('sections.quotaModels.tableHeaders.maxStorageGB')}</th>
                        <th>{t('sections.quotaModels.tableHeaders.notifications')}</th>
                        <th>{t('sections.quotaModels.tableHeaders.isDefault')}</th>
                        <th>{t('sections.quotaModels.tableHeaders.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotaModels.map((model) => (
                        <tr key={model.id}>
                          <td><div className="table-name">{model.name}</div></td>
                          <td><div className="table-label">{model.maxStorageGB} GB</div></td>
                          <td>
                            <div className="table-label">
                              {model.notificationsEnabled
                                ? t('sections.quotaModels.notificationsAt', { percent: model.notificationThresholdPercent })
                                : t('sections.quotaModels.notificationsOff')}
                            </div>
                          </td>
                          <td><div className="table-label">{model.isDefault ? '✓' : '—'}</div></td>
                          <td>
                            <button
                              onClick={() => onOpenEditModal(model)}
                              className="admin-add-btn"
                            >
                              {t('sections.quotaModels.edit')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="errors-section">
                  <p>⚠️ {t('sections.quotaModels.noModels')}</p>
                  <p className="admin-mt-8 admin-text-small">{t('sections.quotaModels.noModelsHint')}</p>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};
